import StringListEditor from "./StringListEditor";
import { QuestionTypeEditorProps } from "./types";

// drag_drop_classification: optionsJson { items[], zones[] },
// correctAnswerJson { [zone]: string[] }. Edited as item -> zone, then grouped.
const ClassificationEditor = ({
  optionsJson,
  correctAnswerJson,
  onChange,
}: QuestionTypeEditorProps) => {
  const items = (optionsJson.items as string[]) ?? [];
  const zones = (optionsJson.zones as string[]) ?? [];
  const grouped = correctAnswerJson as Record<string, string[]>;

  // Flatten { zone: [items] } -> { item: zone } for per-item editing.
  const itemZone: Record<string, string> = {};
  Object.entries(grouped).forEach(([zone, list]) => {
    (list ?? []).forEach((item) => {
      itemZone[item] = zone;
    });
  });

  const regroup = (
    nextItems: string[],
    nextZones: string[],
    map: Record<string, string>
  ): Record<string, string[]> => {
    const out: Record<string, string[]> = {};
    nextZones.filter(Boolean).forEach((z) => (out[z] = []));
    nextItems.forEach((item) => {
      const z = map[item];
      if (z && out[z]) out[z].push(item);
    });
    return out;
  };

  const setItems = (next: string[]) =>
    onChange({
      optionsJson: { items: next, zones },
      correctAnswerJson: regroup(next, zones, itemZone),
    });
  const setZones = (next: string[]) =>
    onChange({
      optionsJson: { items, zones: next },
      correctAnswerJson: regroup(items, next, itemZone),
    });
  const assign = (item: string, zone: string) =>
    onChange({
      optionsJson: { items, zones },
      correctAnswerJson: regroup(items, zones, { ...itemZone, [item]: zone }),
    });

  return (
    <div className="space-y-4">
      <StringListEditor
        label="Items (to classify)"
        values={items}
        onChange={setItems}
        placeholder="e.g. Direct materials"
        addLabel="Add item"
      />
      <StringListEditor
        label="Zones (categories)"
        values={zones}
        onChange={setZones}
        placeholder="e.g. Variable"
        addLabel="Add zone"
      />
      {items.filter(Boolean).length > 0 && zones.filter(Boolean).length > 0 && (
        <div className="space-y-2">
          <label className="block font-medium text-burntBlack text-sm">
            Correct classification
          </label>
          {items.filter(Boolean).map((item) => (
            <div key={item} className="flex items-center gap-3">
              <span className="flex-1 text-sm text-gray-600 truncate">
                {item}
              </span>
              <select
                value={itemZone[item] ?? ""}
                onChange={(e) => assign(item, e.target.value)}
                className="w-48 h-10 px-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              >
                <option value="">— select zone —</option>
                {zones.filter(Boolean).map((zone) => (
                  <option key={zone} value={zone}>
                    {zone}
                  </option>
                ))}
              </select>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ClassificationEditor;
